import React, { useContext } from 'react'
import styled from 'styled-components'
import { graphql } from 'gatsby'

import Button from '../atoms/Button'
import Image from '../atoms/Image'
import Layout from '../organisms/Layout'
import OuterLink from '../atoms/OuterLink'
import ReviewList from '../organisms/ReviewList'
import SEO from '../molecules/SEO'
import useMenu from '../hooks/useMenu'
import ThemeContext from '../contexts/ThemeContext'
import { Query } from '../graphqlTypes'

/**
 * 表示する口コミの表示順
 * @type {number[]}
 */
const SURVEY_ANSWER_ID_ORDERS: number[] = [36, 957, 1001, 683]

const Container = styled.article`
  padding: 0 0 80px;
  margin: 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    padding: 0 0 100px;
  }
`

const Section = styled.section<{ backgroundColor?: string; borderColor?: string }>`
  background: ${({ backgroundColor }) => backgroundColor || 'transparent'};
  border-bottom: ${({ borderColor }) => (borderColor ? `1px solid ${borderColor}` : 0)};
  margin: 20px 16px 0;
  padding: ${({ borderColor }) => (borderColor ? '0 0 50px' : 0)};
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 30px auto 0;
    max-width: 940px;
    &:first-of-type {
      margin: 20px auto 0;
    }
  }
`

const HeadingImage = styled(Image)`
  display: block;
  margin: 0 auto;
  width: 130px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    width: 180px;
  }
`

const Heading = styled.h2`
  color: ${({ theme }) => theme.palette.font.main};
  font-size: 14px;
  font-weight: bold;
  margin: 16px 16px 0;
  text-align: center;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 22px;
  }
`

const MobileLineBreak = styled.br`
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    display: none;
  }
`

const HeadingHighlight = styled.span`
  background: linear-gradient(transparent 63%, ${({ theme }) => theme.palette.primary.veryLight} 63%);
`

const CoverImage = styled(Image)<{ gutterTop?: boolean }>`
  margin: ${({ gutterTop }) => (gutterTop ? '20px' : '10px')} auto 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    width: 850px;
  }
`

const Screenshot = styled(CoverImage)`
  width: 190px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    width: 290px;
  }
`

const Text = styled.p<{ bold?: boolean }>`
  font-size: 14px;
  font-weight: ${({ bold }) => (bold ? 'bold' : 'normal')};
  margin: 16px 0 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    font-size: 16px;
  }
`

const Comment = styled.small`
  color: ${({ theme }) => theme.palette.font.gray};
  display: inline-block;
  font-size: 10px;
  margin: 10px 0 0;
`

const ButtonContainer = styled(OuterLink)`
  display: block;
  margin: 20px auto 0;
  text-decoration: none;
  width: 280px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 34px 0 0 auto;
  }
`

const LaunchIcon = styled(Image)`
  width: 14px;
  height: 14px;
  margin: 0 0 0 11px;
  object-fit: cover;
`

const ForBusiness = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.border.main};
  border-radius: 5px;
  margin: 20px 0 0;
  padding: 20px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 30px 0 0;
    padding: 24px;
  }
`

const ForBusinessText = styled(Text)`
  margin: ${({ bold }) => (bold ? '0' : '10px')} 0 0;
`

const Review = styled(Section)`
  margin: 30px 0 0;
  padding: 20px 16px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 40px -180px 0;
    padding: 32px 0;
    width: 100vw;
    max-width: unset;
  }
`

const ReviewSectionHeading = styled(Heading)`
  margin: 0;
`

const ReviewListContainer = styled.div`
  margin: 10px auto 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 30px auto 0;
    padding: 0;
    width: 940px;
  }
`

const WrappedReviewList = styled(ReviewList)`
  li {
    padding: 16px 16px 20px;
  }
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    align-items: center;
    flex-direction: column;
    flex-wrap: wrap;
    max-height: 980px;
    li {
      padding: 25px 20px;
      width: calc(100% / 2 - 9px);
      &:nth-child(1) {
        margin: 0 9px 9px 0;
      }
      &:nth-child(2) {
        margin: 9px 9px 0 0;
      }
      &:nth-child(3) {
        margin: 0 0 9px 9px;
      }
      &:nth-child(4) {
        margin: 9px 0 0 9px;
      }
    }
  }
  @media (max-width: ${({ theme }) => theme.mediaQuery.mobile.maxWidth}) {
    li {
      &:nth-child(even) {
        display: none;
      }
    }
  }
`

const NewsFeed = styled.iframe`
  margin: 20px 0 0;
  width: 100%;
  height: 590px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    height: 380px;
  }
`

export default function AboutPage({ data }: { data: Query }) {
  const theme = useContext(ThemeContext)
  const { allMysqlCategories, allMysqlProducts, allMysqlRanks, allMysqlSurveyAnswers, site } = data
  const menu = useMenu(
    allMysqlCategories.edges.filter(({ node }) => node.parent_id == null),
    allMysqlRanks.group
  )

  return (
    <Layout hideCampaign hideDescription logoVariant="h1" menu={menu}>
      <SEO
        image={`${process.env.GATSBY_CDN_URL}/${process.env.GATSBY_CDN_PATH}/ogp/top.png`}
        meta={[{ name: 'robot', content: 'index,nofollow' }]}
        title={`ママリ口コミ大賞とは | ${site!.siteMetadata!.title}`}
        type="article"
        url="https://award.mamari.jp/about"
      />
      <Container>
        <Section borderColor={theme.palette.border.main}>
          <HeadingImage alt="ミッション" filename="about/heading-mission.png" />
          <Heading>
            家族の舵取り役である
            <MobileLineBreak />
            &quot;ママ&quot; を支えることを通して、
            <br />
            <HeadingHighlight>家族がもっと幸せになる世の中を作ります</HeadingHighlight>
          </Heading>
          <CoverImage gutterTop alt="家族写真" filename="about/family.png" />
          <Text bold>
            2019年に出産をしたママの3人に1人が使っている、だからこそ。<small>※1</small>
          </Text>
          <Text>
            ママリはこう考えます。
            <br />
            家族にとって最良の選択を自分の意思で行うことが「家族の幸せ」を育むと。
            <br />
            <br />
            子どものお世話、保険、引っ越し、習いごと
            <br />
            家族と過ごす人生は、新しい選択の連続です。
            <br />
            <br />
            人それぞれ個性があるように、家族のスタイルもさまざま。
            <br />
            慌ただしい日々の中で、正解のない選択をし続けることは決して、容易なことではありません。
            <br />
            <br />
            月間150万件のママの声が集まるママリ。
            <br />
            ママの3人に1人が使っているママリ。
            <br />
            <br />
            だからこそ、ママの声に向き合い、寄り添える存在であり続けたいと真剣に思っています。
          </Text>
          <ButtonContainer to="https://mamari.jp">
            <Button color="white">
              ママリをみる
              <LaunchIcon alt="ママリをみる" filename="icon-launch-pink.png" />
            </Button>
          </ButtonContainer>
        </Section>

        <Section>
          <HeadingImage alt="サービス" filename="about/heading-service.png" />
          <Heading>
            ママ向けNo.1 <small>*1</small> アプリ「ママリ」がおくる、
            <br />
            <HeadingHighlight>先輩ママの「本当に使ってよかった」を紹介する取り組み</HeadingHighlight>
            が「ママリ口コミ大賞」です
          </Heading>
          <Screenshot alt="スクリーンショット" filename="screenshot.png" />
          <Text>
            子育てをスタートしたママの毎日は初めてのことだらけ。
            <br />
            その一つが、赤ちゃんや家族のためのお買い物。
            <br />
            ママリの中では、&quot;家族のために後悔しない選択をしたい&quot;
            と願うママ同士の真剣なやりとりが行われています。
            <br />
            <br />
            もしママリに寄せられる先輩ママたちの知見をわかりやすい形でまとめることができたら、これから子育てをするママたちの負担を減らすことができるのではないか。
            <br />
            今よりもっと &quot;ママの一歩を支える&quot; ことができるかもしれない。
            <br />
            <br />
            そんな想いから「ママリ口コミ大賞」は生まれました。
            <br />
            <br />
            子育てをスタートした全国のママとその家族が、納得してお買い物できる未来のために。
            <br />
            2019年に出産したママの3人に1人が会員登録する「ママリ」<small>※2</small> がおくる、
            <br />
            先輩ママの「本当に使ってよかった」を紹介する取り組みが「ママリ口コミ大賞」です。
          </Text>
          <Comment>
            * 1：妊娠中〜2歳0ヶ⽉の⼦供を持つ⼥性1,084⼈を対象とした調査より「現在使っているアプリ(妊娠・⼦育て系)
            をすべて教えてください」で第1位（2019年3⽉実施、調査協⼒：インテージ）
            <br />*
            2：2019年の「ママリ」内の出産予定日を設定したユーザー数と、厚生労働省発表「人口動態統計」の出生数から算出
          </Comment>
          <ForBusiness>
            <ForBusinessText bold>企業の皆様へ</ForBusinessText>
            <ForBusinessText>
              人それぞれ個性があるように、家族のスタイルもさまざまです。
              <br />
              2019年に出産したママの3人に1人が会員登録しているママリ<small>※1</small>
              だからこそ、ママと家族の毎日に真剣に向き合いたい。
              <br />
              そんな想いから「ママリ口コミ大賞」は生まれました。
              <br />
              <br />
              どんなママが選んだ商品で、どんな点に満足しているのか、
              <br />
              どんなライフスタイルのママの口コミなのかをわかりやすく表現し、
              <br />
              特設ページを見るだけで自分と家族に合う商品なのかわかるように工夫しました。
              <br />
              他にも自分と家族にあった商品に出会える工夫をたくさん詰め込んでいます。
              <br />
              <br />
              ママリ口コミ大賞のエンブレムは、毎日を頑張っているママや、
              <br />
              これからママになる女性に対して、全国の先輩ママたちの「本当に使ってよかった」を集めたシンボルであり、
              <br />
              育児などに奮闘する毎日へのエールです。
              <br />
              <br />
              是非店頭やWEBページなどで、ママリ口コミ大賞のエンブレムや口コミをご活用ください。
            </ForBusinessText>
            <ButtonContainer to="http://bit.ly/2M0sadx">
              <Button color="white">
                お問い合わせ
                <LaunchIcon alt="お問い合わせ" filename="icon-launch-pink.png" />
              </Button>
            </ButtonContainer>
          </ForBusiness>
        </Section>

        <Review backgroundColor={theme.palette.primary.pale}>
          <ReviewSectionHeading>ママリ口コミ大賞に届いた先輩ママたちの口コミ</ReviewSectionHeading>
          <ReviewListContainer>
            <WrappedReviewList
              compact
              surveyAnswers={allMysqlSurveyAnswers.edges
                .sort((a, b) => {
                  const numA = SURVEY_ANSWER_ID_ORDERS.indexOf(a.node.mysqlId!)
                  const numB = SURVEY_ANSWER_ID_ORDERS.indexOf(b.node.mysqlId!)
                  return numA - numB
                })
                .map((surveyAnswer) => {
                  const product = allMysqlProducts.edges.find(
                    ({ node }) => surveyAnswer.node.product_id === node.mysqlId
                  )
                  if (!product) throw new TypeError('product is undefined')

                  const category = allMysqlCategories.edges.find(
                    ({ node }) => product.node.category_id === node.mysqlId
                  )
                  if (!category) throw new TypeError('category is undefined')

                  return {
                    ...surveyAnswer,
                    label: `${category.node.title}部門「${product.node.name}」の口コミ`,
                  }
                })}
            />
          </ReviewListContainer>
        </Review>

        <Section>
          <HeadingImage alt="ニュース" filename="about/heading-news.png" />
          <NewsFeed
            className="fw-iframe"
            frameBorder="0"
            scrolling="no"
            src={`https://feed.mikle.com/widget/v2/${process.env.GATSBY_FEEDWIND_WIDGET_ID}`}
          />
          <ButtonContainer to="https://news-award.mamari.jp">
            <Button color="white">
              もっとみる
              <LaunchIcon alt="もっとみる" filename="icon-launch-pink.png" />
            </Button>
          </ButtonContainer>
        </Section>
      </Container>
    </Layout>
  )
}

export const query = graphql`
  query($awardId: Int, $answers: [Int]) {
    allMysqlCategories {
      edges {
        node {
          id
          mysqlId
          parent_id
          slug
          title
          categories {
            id
            mysqlId
            slug
            title
            fields {
              imageUrl
              pagePath
            }
          }
        }
      }
    }
    allMysqlProducts {
      edges {
        node {
          id
          mysqlId
          category_id
          name
        }
      }
    }
    allMysqlRanks(filter: { award_id: { eq: $awardId }, rank: { eq: 1 } }) {
      group(field: category_id) {
        fieldValue
        edges {
          node {
            id
            mysqlId
            category_id
            rank
            products {
              id
              mysqlId
              category_id
              fields {
                imageUrl
              }
            }
          }
        }
      }
    }
    allMysqlSurveyAnswers(filter: { mysqlId: { in: $answers } }) {
      edges {
        node {
          id
          mysqlId
          product_id
          ratings
          comment
          using_time
          users {
            mother_job
            mother_birthday
            youngest_child_birthday
            youngest_child_sex
            other_children
            created
          }
        }
      }
    }
    site {
      siteMetadata {
        title
      }
    }
  }
`
